import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import * as MOCK from "../mock";
import { resolveImageUrl } from "../admin/ImageUpload";

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    settings: null,
    courses: [],
    teachers: [],
    planning: [],
    tarifs: [],
    tenues: [],
    dates: [],
    gallery: [],
    galleryCategories: [],
    boardMembers: [],
    stages: [],
    infos: [],
    scheduleSlots: [],
    pricingTiers: [],
    homeDances: [],
    loaded: false,
    error: null,
  });

  const fetchAll = useCallback(async () => {
    try {
      const { data: res } = await api.get("/content");
      setData({
        settings: res.settings || MOCK.SITE,
        courses: res.courses?.length ? res.courses : MOCK.COURSES,
        teachers: res.teachers?.length ? res.teachers : MOCK.TEACHERS,
        planning: res.planning?.length ? res.planning : MOCK.PLANNING,
        tarifs: res.tarifs?.length ? res.tarifs : MOCK.TARIFS,
        tenues: res.tenues?.length ? res.tenues : MOCK.TENUES,
        dates: res.dates?.length ? res.dates : MOCK.DATES,
        gallery: res.gallery?.length
          ? res.gallery.map((g) => (typeof g === "string" ? { url: g } : { ...g, url: resolveImageUrl(g.url) || g.url || "" }))
          : MOCK.GALLERY.map((u) => ({ url: u })),
        galleryCategories: res.galleryCategories || [],
        boardMembers: (res.boardMembers || []).map((m) => ({ ...m, photo: resolveImageUrl(m.photo) || m.photo || "" })),
        stages: (res.stages || []).map((s) => ({ ...s, image: resolveImageUrl(s.image) || s.image || "" })),
        infos: res.infos?.length ? res.infos : [
          { text: "Si vous appréciez notre école et souhaitez nous donner un coup de pouce en partageant votre expérience.", cta: "Laisser un avis", link: "/contact", color: "bg-blush-light" },
          { text: "Pour consulter les dates importantes de la saison : rentrée, portes ouvertes, semaines à thème, spectacle de fin d'année.", cta: "Voir les dates", link: "/dates-importantes", color: "bg-sky-soft" },
          { text: "Découvrez toutes nos disciplines : éveil, initiation, danse classique, élémentaire, supérieur et cours adultes.", cta: "Découvrir les cours", link: "/cours-tenues", color: "bg-cream-yellow" },
        ],
        scheduleSlots: res.scheduleSlots || [],
        pricingTiers: res.pricingTiers || [],
        homeDances: res.homeDances || [],
        loaded: true,
        error: null,
      });
    } catch (e) {
      console.warn("Failed to fetch content, using mock", e);
      setData({
        settings: MOCK.SITE,
        courses: MOCK.COURSES,
        teachers: MOCK.TEACHERS,
        planning: MOCK.PLANNING,
        tarifs: MOCK.TARIFS,
        tenues: MOCK.TENUES,
        dates: MOCK.DATES,
        gallery: MOCK.GALLERY.map((u) => ({ url: u })),
        galleryCategories: [],
        boardMembers: [],
        stages: [],
        infos: [
          { text: "Si vous appréciez notre école et souhaitez nous donner un coup de pouce en partageant votre expérience.", cta: "Laisser un avis", link: "/contact", color: "bg-blush-light" },
          { text: "Pour consulter les dates importantes de la saison.", cta: "Voir les dates", link: "/dates-importantes", color: "bg-sky-soft" },
          { text: "Découvrez toutes nos disciplines.", cta: "Découvrir les cours", link: "/cours-tenues", color: "bg-cream-yellow" },
        ],
        scheduleSlots: [],
        pricingTiers: [],
        homeDances: [],
        loaded: true,
        error: e.message,
      });
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <DataContext.Provider value={{ ...data, derivedPlanning: deriveFromCourses(data.courses, data.teachers), refetch: fetchAll }}>
      {children}
    </DataContext.Provider>
  );
};

// ------- Helpers -------
const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const deriveFromCourses = (courses = [], teachers = []) => {
  const colorByTeacher = {};
  teachers.forEach((t) => { if (t?.name) colorByTeacher[t.name] = t.color || "bg-stone-200"; });
  const byDay = {};
  courses.forEach((c) => {
    const slots = Array.isArray(c.slots) ? c.slots : [];
    slots.forEach((s) => {
      const dayKey = s.day || s.dayKey;
      const dayLabel = DAY_LABELS[dayKey] || dayKey;
      if (!dayLabel) return;
      if (!byDay[dayKey]) byDay[dayKey] = { dayKey, day: dayLabel, slots: [] };
      const start = s.startTime || "";
      const end = s.endTime || "";
      byDay[dayKey].slots.push({
        time: start && end ? `${start} - ${end}` : (start || end || ""),
        startTime: start,
        course: c.name,
        teacher: s.teacher || c.teacher || "",
        room: s.room || "Salle de danse",
        color: c.color || colorByTeacher[s.teacher || ""] || "bg-stone-200",
        courseId: c.id,
      });
    });
  });
  // sort slots by startTime within each day, then assemble in DAY_ORDER
  const result = DAY_ORDER.filter((d) => byDay[d]).map((d) => {
    const day = byDay[d];
    day.slots.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
    return day;
  });
  return result;
};
